<ng-container *ngIf="applicationDetails">

<!-- ============================================
     Shared field partials - written once, composed into either the dense
     TABLE row layout or the 2-column CARD layout below via
     ngTemplateOutlet, so Status/Match/Salary/Location/Timing/Actions can
     never drift out of sync between the two view modes.
     ============================================ -->

<ng-template #positionTpl>
  <h3 class="job-title" [title]="applicationDetails.job_title">{{ applicationDetails.job_title }}</h3>
  <div class="company-line">
    <i class="bi bi-building"></i>
    <span class="company-name" [title]="applicationDetails.company_name">{{ applicationDetails.company_name }}</span>
    <span class="sep">&middot;</span>
    <span class="app-number">#{{ applicationDetails.id }}</span>
  </div>
</ng-template>

<ng-template #statusTpl>
  <div class="status-badge" [ngClass]="'status-badge-' + applicationDetails.status.toLowerCase()">
    <span class="status-dot" [ngClass]="'dot-' + applicationDetails.status.toLowerCase()"></span>
    <select
      [ngModel]="applicationDetails.status"
      (ngModelChange)="updateStatus($event)"
      class="status-select"
      [title]="getCurrentStatusTitle(applicationDetails.status)"
    >
      <option *ngFor="let status of statusOptions" [value]="status">
        {{ getStatusLabel(status) }}
      </option>
    </select>
    <i class="bi bi-chevron-down chevron-icon"></i>
  </div>
</ng-template>

<ng-template #matchTpl>
  <div
    class="match"
    [ngClass]="{
      'match-high': (applicationDetails.profile_matching_score ?? 0) >= 90,
      'match-medium': (applicationDetails.profile_matching_score ?? 0) >= 70 && (applicationDetails.profile_matching_score ?? 0) < 90,
      'match-low': (applicationDetails.profile_matching_score ?? 0) < 70
    }"
  >
    <div class="match-top">
      <span class="match-chip">
        <i class="bi bi-stars"></i>{{ applicationDetails.profile_matching_score ?? 0 }}%
      </span>
    </div>
    <div class="match-track">
      <div class="match-fill" [style.width.%]="applicationDetails.profile_matching_score ?? 0"></div>
    </div>
    <div class="match-caption">{{ t.LABEL_AI_ANALYZED }}</div>
  </div>
</ng-template>

<ng-template #salaryTpl>
  @if (applicationDetails.salary_min_value && applicationDetails.salary_max_value) {
    <div class="value">{{ formattedSalary.min }} &ndash; {{ formattedSalary.max }}</div>
    <div class="caption" [pTooltip]="applicationDetails.salary_company_message || t.TOOLTIP_NO_MESSAGE">
      {{ t.LABEL_EXPECTED }}: {{ formattedSalary.target }}
    </div>
  } @else {
    <div class="value is-empty">{{ t.LABEL_NOT_SET }}</div>
    <button class="link-btn" (click)="openSalaryDialog()">{{ t.LABEL_ADD_SALARY }}</button>
  }
</ng-template>

<ng-template #locationTpl>
  @if (applicationDetails.company_address) {
    <div class="value location-address" [title]="applicationDetails.company_address">
      @if (activateGooogleMaps) {
        <a [href]="getMapsLink(applicationDetails.company_address)" target="_blank" rel="noopener noreferrer" class="location-link">
          {{ applicationDetails.company_address }}
        </a>
      } @else {
        {{ applicationDetails.company_address }}
      }
    </div>
  } @else {
    <div class="value is-empty">{{ t.LABEL_NOT_SET }}</div>
  }
  <div class="caption">
    <i class="bi bi-globe"></i>
    {{ applicationDetails.job_description_language }}
  </div>
</ng-template>

<!-- Timing (chamba-ai#316): Lead Time always visible; Time to Submit is a
     second row in the SAME block, shown only when the user's
     show_timing_metrics setting is on (chamba-ai-backend-fastapi#181).
     .timing-block / .timing-row class names and row count/order are
     load-bearing for application-summary-card.component.spec.ts - kept
     unchanged regardless of view mode. -->
<ng-template #timingTpl>
  <div class="timing-block">
    <div class="timing-row">
      <span class="timing-row-label" [pTooltip]="t.TOOLTIP_LEAD_TIME" pTooltipShowDelay="500">{{ t.LABEL_LEAD_TIME }}</span>
      @if (applicationDetails.lead_time_seconds !== null) {
        <span class="timing-row-value"><app-elapsed-timer [seconds]="applicationDetails.lead_time_seconds"></app-elapsed-timer></span>
      } @else {
        <span class="timing-row-value is-empty">{{ t.LABEL_LEAD_TIME_PENDING }}</span>
      }
    </div>
    @if (timingMetricsService.isEnabled()) {
      <div class="timing-row">
        <span
          class="timing-row-label"
          [pTooltip]="applicationDetails.time_to_submit_seconds != null ? t.LABEL_TIME_TO_SUBMIT_DONE : t.LABEL_TIME_TO_SUBMIT_IN_PROGRESS"
          pTooltipShowDelay="500"
        >{{ t.LABEL_TIME_TO_SUBMIT }}</span>
        @if (applicationDetails.time_to_submit_seconds != null) {
          <span class="timing-row-value"><app-elapsed-timer [seconds]="applicationDetails.time_to_submit_seconds"></app-elapsed-timer></span>
        } @else if (applicationDetails.time_so_far_seconds != null) {
          <span class="timing-row-value"><app-elapsed-timer [seconds]="applicationDetails.time_so_far_seconds"></app-elapsed-timer></span>
        } @else {
          <span class="timing-row-value is-empty">{{ t.LABEL_NOT_SUBMITTED }}</span>
        }
      </div>
      @if (applicationDetails.submitted_at) {
        <div class="timing-row">
          <span
            class="timing-row-label"
            [pTooltip]="applicationDetails.time_to_first_response_seconds != null ? t.TOOLTIP_TIME_SINCE_SUBMISSION_DONE : t.TOOLTIP_TIME_SINCE_SUBMISSION_IN_PROGRESS"
            pTooltipShowDelay="500"
          >{{ t.LABEL_TIME_SINCE_SUBMISSION }}</span>
          @if (applicationDetails.time_to_first_response_seconds != null) {
            <span class="timing-row-value"><app-elapsed-timer [seconds]="applicationDetails.time_to_first_response_seconds"></app-elapsed-timer></span>
          } @else if (applicationDetails.time_since_submission_so_far_seconds != null) {
            <span class="timing-row-value"><app-elapsed-timer [seconds]="applicationDetails.time_since_submission_so_far_seconds"></app-elapsed-timer></span>
          }
        </div>
      }
    }
  </div>
</ng-template>

<!-- Actions - a real floating dropdown (p-menu, appendTo="body") instead
     of a SpeedDial: SpeedDial expands its action list by pushing outward
     from the trigger button, which has nowhere to go in a narrow table
     column (or a card footer) and either overlaps neighboring content or
     crowds the action icons on top of each other. A popup menu is
     teleported to <body> and positioned by PrimeNG's own overlay logic,
     so it is never constrained by - or overlaps - this row/card's own
     layout, regardless of view mode. -->
<ng-template #actionsTpl let-viewMode="viewMode">
  <button
    type="button"
    class="icon-btn view-details-btn"
    [class.view-details-btn-labeled]="viewMode === 'card'"
    (click)="openWizardForThisApplication()"
    [attr.aria-label]="t.BTN_VIEW_DETAILS"
    [pTooltip]="t.BTN_VIEW_DETAILS"
    pTooltipShowDelay="400"
  >
    <i class="bi bi-eye"></i>
    <span *ngIf="viewMode === 'card'" class="view-details-label">{{ t.BTN_VIEW_DETAILS }}</span>
  </button>

  @if (applicationDetails.last_follow_up) {
    <button
      class="icon-btn"
      [ngClass]="{'flagged': applicationDetails.last_follow_up}"
      (click)="openFollowUpDialog()"
      [pTooltip]="applicationDetails.last_follow_up ? applicationDetails.last_follow_up : t.TOOLTIP_FLAG_PRIORITY"
      [tooltipStyleClass]="'multiline-tooltip'"
    >
      <i class="bi bi-flag-fill"></i>
    </button>
  }

  <button
    type="button"
    class="icon-btn"
    (click)="moreMenu.toggle($event)"
    [attr.aria-label]="t.TOOLTIP_MORE_ACTIONS"
    [pTooltip]="t.TOOLTIP_MORE_ACTIONS"
    pTooltipShowDelay="400"
    aria-haspopup="true"
  >
    <i class="bi bi-three-dots"></i>
  </button>
  <p-menu #moreMenu [model]="items" [popup]="true" appendTo="body" styleClass="app-card-more-menu">
    <ng-template pTemplate="item" let-item>
      <button type="button" class="more-menu-item" (click)="item.command()">
        <span [class]="item.icon + ' ' + item.iconColor"></span>
        <span class="more-menu-item-label">{{ item.label }}</span>
      </button>
    </ng-template>
  </p-menu>
</ng-template>

<ng-template #labelsTpl>
  <app-application-labels
    [applicationId]="applicationDetails.id"
    [assignedLabels]="applicationDetails.labels || []"
    (labelsChanged)="onLabelsChanged($event)"
  ></app-application-labels>
</ng-template>

<!-- ============================================
     TABLE mode - dense row, aligned to applications-table's shared
     --jt-row-grid column template.
     ============================================ -->
@if (viewMode === 'table') {
  <div class="jt-row" [ngClass]="'jt-row-status-' + applicationDetails.status.toLowerCase()">
    <div class="jt-row-grid">
      <div class="jt-cell jt-cell-position"><ng-container *ngTemplateOutlet="positionTpl"></ng-container></div>
      <div class="jt-cell jt-cell-status"><ng-container *ngTemplateOutlet="statusTpl"></ng-container></div>
      <div class="jt-cell jt-cell-match"><ng-container *ngTemplateOutlet="matchTpl"></ng-container></div>
      <div class="jt-cell jt-cell-salary"><ng-container *ngTemplateOutlet="salaryTpl"></ng-container></div>
      <div class="jt-cell jt-cell-location"><ng-container *ngTemplateOutlet="locationTpl"></ng-container></div>
      <div class="jt-cell jt-cell-timing"><ng-container *ngTemplateOutlet="timingTpl"></ng-container></div>
      <div class="jt-cell jt-cell-actions"><ng-container *ngTemplateOutlet="actionsTpl; context: { viewMode: 'table' }"></ng-container></div>
    </div>
    <div class="jt-labels-row"><ng-container *ngTemplateOutlet="labelsTpl"></ng-container></div>
  </div>
}

<!-- ============================================
     CARD mode - rendered by applications-table inside a 2-column grid.
     ============================================ -->
@if (viewMode === 'card') {
  <div class="app-card" [ngClass]="'card-status-' + applicationDetails.status.toLowerCase()">
    <div class="card-accent" [ngClass]="'card-accent-' + applicationDetails.status.toLowerCase()"></div>
    <div class="card-content">
      <div class="card-header">
        <div class="card-header-left"><ng-container *ngTemplateOutlet="positionTpl"></ng-container></div>
        <ng-container *ngTemplateOutlet="statusTpl"></ng-container>
      </div>

      <div class="card-metadata-grid">
        <div class="metadata-block metadata-block-match"><ng-container *ngTemplateOutlet="matchTpl"></ng-container></div>
        <div class="metadata-block"><ng-container *ngTemplateOutlet="salaryTpl"></ng-container></div>
        <div class="metadata-block"><ng-container *ngTemplateOutlet="locationTpl"></ng-container></div>
        <div class="metadata-block"><ng-container *ngTemplateOutlet="timingTpl"></ng-container></div>
      </div>

      <ng-container *ngTemplateOutlet="labelsTpl"></ng-container>

      <div class="card-footer">
        <ng-container *ngTemplateOutlet="actionsTpl; context: { viewMode: 'card' }"></ng-container>
      </div>
    </div>
  </div>
}

<!-- Map tooltip template (hidden, used by tooltip) -->
<ng-template #mapTooltipTemplate>
  <div class="map-tooltip-content">
    <div class="map-tooltip-address">{{ applicationDetails.company_address }}</div>
    <iframe
      width="300"
      height="180"
      frameborder="0"
      style="border-radius: 8px; border: 0; min-height: 120px"
      [src]="getMapUrl(applicationDetails.company_address)"
      allowfullscreen
      [attr.aria-label]="t.ARIA_COMPANY_LOCATION_MAP"
    ></iframe>
  </div>
</ng-template>

<!-- Cover Letter Table -->
<app-cover-letter-table
  *ngIf="showCoverLetterTable"
  [applicationId]="applicationDetails.id"
></app-cover-letter-table>

<!-- Curriculum Vitae Table -->
<app-curriculum-vitae-table
  *ngIf="showCurriculumVitaeTable"
  [applicationId]="applicationDetails.id"
></app-curriculum-vitae-table>

<!-- Salary & Message Editor Dialog -->
<p-dialog
  [header]="t.DIALOG_SALARY_HEADER"
  [(visible)]="showSalaryDialog"
  [modal]="true"
  [closable]="true"
  [dismissableMask]="true"
  (onHide)="closeSalaryDialog()"
  [style]="{ width: '720px', maxWidth: '96vw' }"
  styleClass="salary-dialog"
  appendTo="body"
>
  <app-job-salary
    [applicationId]="applicationDetails.id"
    [jobTitle]="applicationDetails.job_title"
    [companyName]="applicationDetails.company_name"
    [salary_min_value]="salaryFields.salary_min_value"
    [salary_max_value]="salaryFields.salary_max_value"
    [salary_target_value]="salaryFields.salary_target_value"
    [salary_company_message]="salaryFields.salary_company_message"
    (salaryUpdated)="handleSalaryUpdated($event)"
    (cancel)="closeSalaryDialog()"
  ></app-job-salary>
</p-dialog>

<!-- Invitation Management Dialog -->
<p-dialog
  [header]="t.DIALOG_INVITATIONS_HEADER"
  [(visible)]="showInvitationDialog"
  [modal]="true"
  [closable]="true"
  [dismissableMask]="true"
  (onHide)="closeInvitationDialog()"
  [style]="{ width: '600px' }"
  appendTo="body"
>
  <app-job-invitation
    *ngIf="showInvitationDialog"
    [applicationId]="applicationDetails.id"
    [jobTitle]="applicationDetails.job_title"
    [companyName]="applicationDetails.company_name"
    (cancel)="closeInvitationDialog()"
  ></app-job-invitation>
</p-dialog>

<!-- Follow Up Dialog -->
<p-dialog
  [header]="t.DIALOG_FOLLOW_UP_HEADER"
  [(visible)]="showFollowUpDialog"
  [modal]="true"
  [closable]="true"
  [dismissableMask]="true"
  [style]="{ width: '400px' }"
  (onHide)="closeFollowUpDialog()"
  appendTo="body"
>
  <app-follow-up
    [applicationId]="applicationDetails.id"
    [jobTitle]="applicationDetails.job_title"
    [companyName]="applicationDetails.company_name"
    (submitFollowUp)="handleFollowUpSubmit($event)"
    (cancel)="closeFollowUpDialog()"
  ></app-follow-up>
  <div *ngIf="isSubmittingFollowUp" class="loading-overlay">
    <span>{{ t.OVERLAY_SUBMITTING }}</span>
  </div>
</p-dialog>

</ng-container>

<div *ngIf="isLoading">{{ t.LOADING }}</div>
<div *ngIf="error">{{ error }}</div>
